import React, { useCallback, useEffect, useState } from 'react';
import { StaticContext } from 'react-router';
import { Link, RouteComponentProps } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import axios from 'axios';
import { OrdersRatingItem } from '../../types';
import { SCREENS } from '~/constants/screens';
import { localeStore } from '~/mobx/localesStore';
import { Table } from '~/view/components/Table/Table';
import Text, { TEXT } from '~/view/components/Text/Text';
import Headers from '~/view/containers/Headers/Headers';
import HeaderBack from '~/view/containers/Headers/components/HeaderBack/HeaderBack';
import { requests } from '~/requests';
import Preloader from '~/view/components/Controls/Preloader';
import ScrollableBlock from '~/view/containers/ScrollableBlock/ScrollableBlock';
import { historyPushWithSaveBackPath } from '~/helpers';
import './style.scss';
import { modalStore } from '~/mobx/modalStore';

type TOrderRatingScreenProps = RouteComponentProps<{ id?: string }, StaticContext, OrdersRatingItem>;

const OrderRatingScreen = ({ match }: TOrderRatingScreenProps) => {
  const [orderRating, setOrderRating] = useState<OrdersRatingItem>({
    id: '',
    average: '',
    meal: '',
    service: '',
    overall: '',
    comment: '',
    createdAt: '',
    commentCreatedAt: '',
    orderId: '',
    orderKey: '',
    userPhone: '',
    userId: '',
    userIsDeleted: false,
    userName: '',
    branchName: '',
  });

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (match.params?.id) {
      fetchOrderRating(match.params.id);
    }
  }, []);

  const fetchOrderRating = useCallback((id: string) => {
    const asyncFetchOrderRating = async () => {
      setLoading(true);
      try {
        const _orderRating = await requests.orderRating.fetchOrderRating(id);
        setOrderRating(_orderRating);
        setLoading(false);
      } catch (error) {
        if (axios.isAxiosError(error) && error.response?.status === 403) {
          // @ts-ignore
          modalStore.openErrorModal({
            title: localeStore.t('error_try_again'),
            message: localeStore.t('possible_you_dont_have_permissions_or_page_not_found'),
          });
        }
        console.error(error);
        setLoading(false);
      }
    };
    asyncFetchOrderRating();
  }, []);

  const handleOpenProfile: React.MouseEventHandler<HTMLAnchorElement> = useCallback(
    (e) => {
      e.preventDefault();
      historyPushWithSaveBackPath(SCREENS.CRM_PROFILE_EDIT(orderRating.userId));
    },
    [orderRating.userId]
  );

  return (
    <Table>
      <Headers>
        <HeaderBack title="order_rating_details_page_hdr" path={SCREENS.ORDERS_RATING()} />
      </Headers>
      <ScrollableBlock showScrollbar className="full-height">
        <Text type={TEXT.SECTION_HEADER} text="order_rating_details_main_info_block_hdr" className="pt-6 text-uppercase" />
        <div className="d-flex flex-row pt-4">
          <div className="d-flex flex-column gap-5">
            <Text type={TEXT.BLOCK_HEADER} text={localeStore.t('order_rating_details_customer_info_title')} className="text-size-10 text-uppercase" />
            {orderRating.userPhone ? (
              <Link onClick={handleOpenProfile} to="#" className="color-mainblue text-size-16 family-semibold">
                {`${orderRating.userName || ''} ${orderRating.userPhone}`.trim()}
              </Link>
            ) : (
              <Text text="order_rating_details_customer_info_deleted_txt" className="text-size-14 family-regular text-uppercase" type={TEXT.CUSTOM} />
            )}
          </div>
          <div className="d-flex flex-column pl-10 gap-5">
            <Text type={TEXT.BLOCK_HEADER} text={localeStore.t('order_rating_details_order_info_title')} className="text-size-10 text-uppercase" />
            {orderRating.orderId ? (
              <Link to={SCREENS.ORDER_RATING_ORDER(orderRating.orderId)} className="color-mainblue text-size-16 family-semibold">
                {orderRating.orderKey}
              </Link>
            ) : (
              <Text text="-" className="text-size-14 family-regular text-uppercase" type={TEXT.CUSTOM} />
            )}
          </div>
        </div>
        <Text type={TEXT.SECTION_HEADER} text="order_rating_details_rating_block_hdr" className="pt-6 text-uppercase" />

        <div className="d-flex flex-row pt-4 gap-30">
          <div className="d-flex flex-column gap-5">
            <div className="d-flex flex-column gap-5">
              <Text type={TEXT.BLOCK_HEADER} text={localeStore.t('crm_order_rating_report_avg_rating_col')} className="text-size-10 text-uppercase" />
              <Text type={TEXT.BLOCK_HEADER} text={orderRating.average} className="text-size-16 font-weight-600" />
            </div>
          </div>
          <div className="order_rating_details__divider" />
          <div className="d-flex flex-column gap-5">
            <Text type={TEXT.BLOCK_HEADER} text={localeStore.t('consumer_order_rating_rate_meal')} className="text-size-10 text-uppercase" />
            <Text type={TEXT.BLOCK_HEADER} text={orderRating.meal} className="text-size-16 family-semibold" />
          </div>
          <div className="d-flex flex-column gap-5">
            <Text type={TEXT.BLOCK_HEADER} text={localeStore.t('consumer_order_rating_rate_service')} className="text-size-10 text-uppercase" />
            <Text type={TEXT.BLOCK_HEADER} text={orderRating.service} className="text-size-16 family-semibold" />
          </div>
          <div className="d-flex flex-column gap-5">
            <Text type={TEXT.BLOCK_HEADER} text={localeStore.t('consumer_order_rating_rate_experience')} className="text-size-10 text-uppercase" />
            <Text type={TEXT.BLOCK_HEADER} text={orderRating.overall} className="text-size-16 family-semibold" />
          </div>
        </div>

        <Text type={TEXT.SECTION_HEADER} text="order_rating_details_comment_block_hdr" className="pt-6 text-uppercase" />

        <Text
          type={TEXT.CUSTOM}
          text={orderRating.comment || ' '}
          className="text-size-20 font-weight-400 pt-4 order_rating__comment line-height-lg"
        />

        <div className="d-flex flex-row flex-wrap gap-60"></div>
      </ScrollableBlock>
      <Preloader visible={loading} />
    </Table>
  );
};

export default observer(OrderRatingScreen);
